/** Shared waterfall geometry. Units are metres except RiverPoint elevations.
 * The suspended sheet is deliberately separate from the standing pool surface. */
import type { RiverPoint, RiverPolyline } from './rivers.js';
import * as pmath from './pmath.js';

/** v12: finite tiered escarpments and explicit close-cascade ownership. */
export const HYDROLOGY_VERSION = 12;
/** Hard terrain edit budget: routes must fit the landscape, including at falls. */
export const RIVER_MAX_CUT_M = 12;
export const FALL_LIP_RUN_M = 0.8;

export interface RiverFall {
  id: string;
  riverId: number;
  lip: RiverPoint;
  end: RiverPoint;
  dropM: number;
  runM: number;
  gravity: number;
  flightTime: number;
  half: number;
  /** A resolved pre-existing cliff, rather than a bounded incision in a slope. */
  nativeCliff?: boolean;
  /** Finite shared cascade formation; never a decorative-only cliff. */
  escarpmentId?: string;
}

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x-a)/(b-a)));
  return t*t*(3-2*t);
};

export function fallInfluence(fall: RiverFall, s: number): number {
  return smooth(fall.lip.s-65, fall.lip.s-18, s) * (1-smooth(fall.end.s+35, fall.end.s+80, s));
}

/** Same surface gravity as the character; shared so a sheet and a swimmer fall together. */
export function waterGravity(radius: number): number {
  return 16 * Math.min(1.4, Math.max(0.55, radius / 2500));
}

export function riverPointBetween(a: RiverPoint, b: RiverPoint, t: number): RiverPoint {
  const lerp = (x: number, y: number) => x + (y - x) * t;
  const x = lerp(a.x, b.x), y = lerp(a.y, b.y), z = lerp(a.z, b.z);
  const l = pmath.hypot(x, y, z);
  const fx = lerp(a.fx, b.fx), fy = lerp(a.fy, b.fy), fz = lerp(a.fz, b.fz);
  const fl = pmath.hypot(fx, fy, fz) || 1;
  return {
    x: x / l, y: y / l, z: z / l, fx: fx / fl, fy: fy / fl, fz: fz / fl,
    ws: lerp(a.ws, b.ws), bed: lerp(a.bed, b.bed), half: lerp(a.half, b.half),
    speed: lerp(a.speed, b.speed), s: lerp(a.s, b.s)
  };
}

/** Resolve cliffs before the ordinary sloping-bed route test. A 10 m source
 * interval can straddle a vertical face: interpolating its bed would excavate
 * the upper plateau, rejecting the entire river before falls were considered.
 * Also repair an unsupported suspended ramp when the face lies near the start
 * of the source interval. Ordinary terrain-following routes stay unchanged. */
export function fitNativeRiverCliffs(
  river: RiverPolyline, radius: number, amplitude: number, seaRel: number,
  native: (x:number,y:number,z:number)=>number,
  excluded?: (x:number,y:number,z:number)=>boolean,
): {river:RiverPolyline;falls:RiverFall[]} {
  const points=river.points.map(p=>({...p})),falls:RiverFall[]=[];
  const height=(p:RiverPoint)=>native(p.x,p.y,p.z);
  const excludedAt=(p:RiverPoint)=>excluded?.(p.x,p.y,p.z)??false;
  const gravity=waterGravity(radius);
  const shifted=(p:RiverPoint,fx:number,fy:number,fz:number,m:number):RiverPoint=>{
    const x=p.x+fx*m/radius,y=p.y+fy*m/radius,z=p.z+fz*m/radius,l=pmath.hypot(x,y,z),dx=x/l,dy=y/l,dz=z/l;
    const radial=fx*dx+fy*dy+fz*dz,tx=fx-radial*dx,ty=fy-radial*dy,tz=fz-radial*dz,tl=pmath.hypot(tx,ty,tz)||1;
    return{...p,x:dx,y:dy,z:dz,fx:tx/tl,fy:ty/tl,fz:tz/tl,s:p.s+m};
  };
  for(let i=1;i<points.length&&falls.length<2;i++){
    const a=points[i-1],b=points[i],ds=b.s-a.s,drop=(a.ws-b.ws)*amplitude;
    if(drop<8||drop>120||ds<1||ds>24||a.s<80||points.at(-1)!.s-b.s<80||
      (b.ws-seaRel)*amplitude<4||falls.some(f=>a.s-f.end.s<180))continue;
    let ordinaryFails=false,ordinaryAir=false;
    for(let j=1;j<4;j++){
      const p=riverPointBetween(a,b,j/4);
      const gap=(height(p)-p.bed)*amplitude;
      if(gap>6.5){ordinaryFails=true;break;}
      if(gap < -1)ordinaryAir=true;
    }
    if(!ordinaryFails&&!ordinaryAir)continue;
    // A real face concentrates most of the drop into a short, steep interval.
    // This must not turn an ordinary long mountain slope into a tall fall.
    const n=Math.max(4,Math.ceil(ds/.25)),profile=Array.from({length:n+1},(_,j)=>riverPointBetween(a,b,j/n));
    const heights=profile.map(height),deltas=heights.slice(1).map((h,j)=>(heights[j]-h)*amplitude);
    let peak=0;for(let j=1;j<n;j++)if(deltas[j]>deltas[peak])peak=j;
    const threshold=Math.max(2*ds/n,deltas[peak]*.1);
    let first=peak,last=peak;while(first>0&&deltas[first-1]>=threshold)first--;while(last<n-1&&deltas[last+1]>=threshold)last++;
    const faceDrop=(heights[first]-heights[last+1])*amplitude,faceRun=(last+1-first)*ds/n;
    if(faceDrop<8||faceDrop<drop*.6||faceRun>Math.max(1,faceDrop*.18))continue;
    // Resolve the beginning of the face below the quarter-metre search grid.
    // Launching even 20 cm early at a tall cliff would spend the entire cut
    // budget excavating its intact upper lip.
    let lipLo=first/n,lipHi=(first+1)/n;
    for(let j=0;j<10;j++){
      const mid=(lipLo+lipHi)*.5,p=riverPointBetween(a,b,mid);
      if((heights[first]-height(p))*amplitude>.15)lipHi=mid;else lipLo=mid;
    }
    const lip=riverPointBetween(a,b,lipLo);
    lip.ws=Math.min(a.ws,height(lip)-1.8/amplitude);lip.bed=lip.ws-(a.ws-a.bed);
    // The source tracer has only 16 headings. Align a short local approach
    // with the native face so both lip banks stay on its upper terrace. Merely
    // skipping their containment test would leave a hanging standing apron.
    const face=riverPointBetween(profile[peak],profile[peak+1],.5);
    const sx=face.fy*face.z-face.fz*face.y,sy=face.fz*face.x-face.fx*face.z,sz=face.fx*face.y-face.fy*face.x,sl=pmath.hypot(sx,sy,sz)||1;
    // Locate the same mid-face contour on both sides. A finite difference at
    // the coarse midpoint can entirely miss a nearly vertical step, or return
    // a false 45 degree normal when both probes cross that discontinuity.
    const contour=(heights[peak]+heights[peak+1])*.5,acrossM=Math.min(1,lip.half*.25),search=Math.max(1,faceRun);
    const contourOffset=(off:number):number|null=>{
      const center=shifted(face,sx/sl,sy/sl,sz/sl,off);
      let lo=-search,hi=search;
      if(height(shifted(center,face.fx,face.fy,face.fz,lo))<contour||height(shifted(center,face.fx,face.fy,face.fz,hi))>contour)return null;
      for(let j=0;j<16;j++){const mid=(lo+hi)*.5;if(height(shifted(center,face.fx,face.fy,face.fz,mid))>contour)lo=mid;else hi=mid;}
      return(lo+hi)*.5;
    };
    const left=contourOffset(-acrossM),right=contourOffset(acrossM);if(left===null||right===null)continue;
    const across=-(right-left)/(2*acrossM);
    let nx=face.fx+sx/sl*across,ny=face.fy+sy/sl*across,nz=face.fz+sz/sl*across;
    const nr=nx*lip.x+ny*lip.y+nz*lip.z;nx-=nr*lip.x;ny-=nr*lip.y;nz-=nr*lip.z;
    const nl=pmath.hypot(nx,ny,nz);if(nl<1e-12)continue;nx/=nl;ny/=nl;nz/=nl;
    if(nx*lip.fx+ny*lip.fy+nz*lip.fz<.866)continue;
    lip.fx=nx;lip.fy=ny;lip.fz=nz;
    // Keep the entire lip on the top side despite submillimetre contour
    // search error. This 2.5 cm launch margin still obeys the same cut limit.
    Object.assign(lip,shifted(lip,nx,ny,nz,-.025));nx=lip.fx;ny=lip.fy;nz=lip.fz;
    lip.ws=Math.min(a.ws,height(lip)-1.8/amplitude);lip.bed=lip.ws-(a.ws-a.bed);
    const approachRun=Math.min(3,lip.s-points[Math.max(0,i-2)].s-.5);
    if(approachRun<.5)continue;
    const approach=shifted(lip,nx,ny,nz,-approachRun);
    approach.ws=Math.min(a.ws,height(approach)-1.8/amplitude);approach.bed=approach.ws-(a.ws-a.bed);
    if(approach.ws<lip.ws)continue;
    // Replace the last coarse approach point too. Keeping it only a few
    // metres from an oblique lip creates a sharp connector turn whose wide
    // cross-section can fall off the cliff before the aligned discharge.
    const beginIndex=i-1;
    // A lip inside the source interval may need the next original downstream
    // point to contain the landing. Retain its actual lower water elevation.
    let accepted:RiverFall|undefined,endIndex=i;
    for(let k=i;k<Math.min(points.length,i+4);k++){
      const originalEnd=points[k],end=shifted(lip,nx,ny,nz,originalEnd.s-lip.s);
      end.ws=originalEnd.ws;end.bed=originalEnd.bed;end.half=originalEnd.half;end.speed=originalEnd.speed;
      const movement=pmath.hypot(end.x-originalEnd.x,end.y-originalEnd.y,end.z-originalEnd.z)*radius;
      if(movement>Math.min(12,lip.half*2))continue;
      const fallDrop=(lip.ws-end.ws)*amplitude;
      if(fallDrop<8||fallDrop>120||end.s-lip.s>45||points.at(-1)!.s-end.s<80)break;
      const flightTime=Math.sqrt(2*fallDrop/gravity),runM=lip.speed*flightTime;
      if(runM<1.6||runM>end.s-lip.s-2)continue;
      const candidate:RiverFall={id:`${river.id}:cliff:${a.s.toFixed(3)}`,riverId:river.id,lip,end:{...end},dropM:fallDrop,runM,gravity,flightTime,half:lip.half,nativeCliff:true};
      let fits=true;
      const count=Math.ceil((end.s-lip.s)/.25);
      for(let j=0;j<=count;j++){
        const p=riverPointBetween(lip,end,j/count),bed=fallBed(candidate,p.s);
        if(excludedAt(p)||(height(p)-bed)*amplitude>RIVER_MAX_CUT_M-.5){fits=false;break;}
        // A lower pool needs real banks, including between original points.
        if(p.s-lip.s<FALL_LIP_RUN_M)continue;
        const sx=p.fy*p.z-p.fz*p.y,sy=p.fz*p.x-p.fx*p.z,sz=p.fx*p.y-p.fy*p.x,sl=pmath.hypot(sx,sy,sz)||1;
        for(const side of [-1,1]){
          const off=side*p.half*1.9/radius,x=p.x+sx/sl*off,y=p.y+sy/sl*off,z=p.z+sz/sl*off,l=pmath.hypot(x,y,z);
          if(native(x/l,y/l,z/l)<end.ws||excluded?.(x/l,y/l,z/l)){fits=false;break;}
        }
        if(!fits)break;
      }
      // Verify the complete ballistic arc, including its last approach to the
      // lower pool. Carving remains bounded; no air interval becomes buoyant.
      for(let j=2;fits&&j<40;j++){
        const p=fallSheetPoint(candidate,j/40,amplitude),bed=fallBed(candidate,p.s),ground=Math.min(height(p),bed);
        if((p.ws-ground)*amplitude<.25)fits=false;
      }
      if(fits){accepted=candidate;endIndex=k;break;}
    }
    if(!accepted)continue;
    points.splice(beginIndex,endIndex-beginIndex+1,approach,lip,accepted.end);falls.push(accepted);
    // Arc coordinates describe the actual short reroute, including the return
    // to the unchanged downstream route. Stored fall endpoints share these
    // point objects, so later supported cliffs cannot invalidate their s.
    for(let j=beginIndex;j<points.length;j++){
      const p=points[j],prev=points[j-1];
      p.s=prev.s+pmath.acos(Math.max(-1,Math.min(1,p.x*prev.x+p.y*prev.y+p.z*prev.z)))*radius;
    }
    for(const j of [beginIndex-1,beginIndex+2]){
      const p=points[j],next=points[j+1];if(!next)continue;
      let fx=next.x-p.x,fy=next.y-p.y,fz=next.z-p.z;
      const radial=fx*p.x+fy*p.y+fz*p.z;fx-=radial*p.x;fy-=radial*p.y;fz-=radial*p.z;
      const fl=pmath.hypot(fx,fy,fz)||1;p.fx=fx/fl;p.fy=fy/fl;p.fz=fz/fl;
    }
    i=beginIndex+2;
  }
  return{river:falls.length?{...river,points}:river,falls};
}

/** Bounded, separated falls at existing steep inland drops. No random draws or changed routes. */
export function buildRiverFalls(rivers: readonly RiverPolyline[], radius: number, amplitude: number, seaRel: number, naturalHeight?: (x:number,y:number,z:number)=>number): RiverFall[] {
  const falls: RiverFall[] = [];
  for (const river of rivers) {
    let lastEnd = -Infinity, count = 0;
    for (let i = 1; i < river.points.length && count < 2; i++) {
      const a = river.points[i - 1], b = river.points[i];
      const ds = b.s - a.s, dropM = (a.ws - b.ws) * amplitude;
      if (dropM < 6 || dropM > 35 || ds < 8 || dropM / ds < 0.6 || a.s < 80 ||
        river.points[river.points.length - 1].s - b.s < 80 || a.s - lastEnd < 180 ||
        (b.ws - seaRel) * amplitude < 4) continue;
      const gravity = waterGravity(radius), flightTime = Math.sqrt(2 * dropM / gravity);
      const runM = a.speed * flightTime;
      if (runM < FALL_LIP_RUN_M * 2 || runM > ds - 2) continue;
      const half=a.half;
      const fall:RiverFall = {
        id: `${river.id}:fall:${i - 1}`, riverId: river.id, lip: { ...a }, end: { ...b },
        dropM, runM, gravity, flightTime, half,
      };
      // The lower pool must fit under the native cliff. Do not turn a long
      // mountain slope into a vertical wall just to exhibit a waterfall.
      let fits=true;
      if(naturalHeight)for(let j=1;j<=20;j++){
        const p=riverPointBetween(a,b,j/20);
        if((naturalHeight(p.x,p.y,p.z)-fallBed(fall,p.s))*amplitude>RIVER_MAX_CUT_M-.5){fits=false;break;}
      }
      if(!fits)continue;
      falls.push(fall);
      lastEnd = b.s; count++;
    }
  }
  return falls;
}

/** Bed turns down just beyond the lip. Standing water below the sheet belongs to the lower pool. */
export function fallBed(fall: RiverFall, s: number): number {
  const k = Math.max(0, Math.min(1, (s - fall.lip.s) / FALL_LIP_RUN_M));
  return fall.lip.bed + (fall.end.bed - fall.lip.bed) * k * k * (3 - 2 * k);
}

/** Ballistic sheet sample; t=0 joins the lip, t=1 enters the receiving pool. */
export function fallSheetPoint(fall: RiverFall, t: number, amplitude: number): RiverPoint {
  const p = riverPointBetween(fall.lip, fall.end, t * fall.runM / (fall.end.s - fall.lip.s));
  p.ws = fall.lip.ws - fall.dropM * t * t / amplitude;
  p.speed = pmath.hypot(fall.lip.speed, fall.gravity * fall.flightTime * t);
  p.half = fall.half;
  return p;
}

/** Ribbon rows include the lower pool's head; no triangle interpolates a swimmable ramp down the fall. */
export function riverSurfaceRuns(river: RiverPolyline, falls: readonly RiverFall[]): RiverPoint[][] {
  const runs: RiverPoint[][] = [[]];
  const dense:RiverPoint[]=[];
  for(let i=0;i<river.points.length-1;i++){
    const a=river.points[i],b=river.points[i+1];
    const near=falls.some(f=>f.riverId===river.id && b.s>f.lip.s-65 && a.s<f.end.s+80);
    // Bank headlands vary over ~14 m. Resolve their wet edge at ≤2.5 m rather
    // than bridging across a ledge with a long, straight water triangle.
    const steps=Math.max(1,Math.ceil((b.s-a.s)/(near?1:2.5)));
    for(let j=0;j<steps;j++)dense.push(j===0?a:riverPointBetween(a,b,j/steps));
  }
  dense.push(river.points[river.points.length-1]);
  for (const p of dense) {
    if(falls.some(f=>f.riverId===river.id && p.s>f.lip.s && p.s<f.end.s))continue;
    runs[runs.length - 1].push(p);
    const fall = falls.find(f => f.riverId === river.id && f.lip.s === p.s);
    if (!fall) continue;
    const start = riverPointBetween(fall.lip, fall.end, FALL_LIP_RUN_M / (fall.end.s - fall.lip.s));
    start.ws = fall.end.ws; start.bed = fall.end.bed;
    const receiver=[start];
    // The lower surface is horizontal below the ballistic sheet. Retain
    // interior rows so physically widened receiving bowls do not collapse to
    // their narrow start/end widths when the renderer finds each wet edge.
    const steps=Math.max(1,Math.ceil(fall.end.s-start.s));
    for(let j=1;j<steps;j++){
      const p=riverPointBetween(start,fall.end,j/steps);
      p.ws=fall.end.ws; p.bed=fall.end.bed;
      receiver.push(p);
    }
    runs.push(receiver);
  }
  return runs;
}
